import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import redditApi from '../../services/redditApi';
import './reddit.css';

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new link.');
    }
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await redditApi.post('/auth/password-reset/confirm/', {
        token,
        password,
        confirm_password: confirm,
      });
      setDone(true);
      setTimeout(() => navigate('/reddit/login', { state: { invited: true } }), 2500);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setBusy(false);
    }
  }

  const pwMatch = confirm && password === confirm;
  const pwMismatch = confirm && password !== confirm;

  return (
    <div className="rh-auth-page">
      <div className="rh-auth-card">
        <div className="rh-auth-brand">
          <div className="rh-brand-icon">R</div>
          <div>
            <h1 className="rh-auth-title">New Password</h1>
            <p className="rh-auth-subtitle">Reddit Campaign Hub</p>
          </div>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <svg width="26" height="26" fill="none" stroke="#16a34a" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <p style={{ margin: '0 0 6px', fontWeight: 600, color: 'var(--rh-text)' }}>Password updated!</p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text-muted)' }}>Redirecting to sign in…</p>
          </div>
        ) : (
          <>
            {error && <div className="rh-auth-error">{error}</div>}
            {!error && (
              <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--rh-text-muted)', lineHeight: 1.6 }}>
                Choose a new password for your account.
              </p>
            )}
            <form onSubmit={onSubmit} className="rh-auth-form">
              <div className="rh-field">
                <label htmlFor="rp-password">New Password</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    id="rp-password"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={!token}
                    style={{ width: '100%', paddingRight: 42, boxSizing: 'border-box' }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    style={{
                      position: 'absolute', right: 12,
                      background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rh-text-muted)',
                      display: 'flex', alignItems: 'center', padding: 0, lineHeight: 1,
                    }}
                    tabIndex={-1}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showPw} />
                  </button>
                </div>
              </div>
              <div className="rh-field">
                <label htmlFor="rp-confirm">Confirm Password</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    id="rp-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repeat password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    disabled={!token}
                    style={{
                      width: '100%', paddingRight: 42, boxSizing: 'border-box',
                      borderColor: pwMismatch ? '#ef4444' : pwMatch ? '#22c55e' : undefined,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    style={{
                      position: 'absolute', right: 12,
                      background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rh-text-muted)',
                      display: 'flex', alignItems: 'center', padding: 0, lineHeight: 1,
                    }}
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
                {pwMismatch && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#ef4444' }}>Passwords do not match</p>
                )}
                {pwMatch && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#16a34a' }}>Passwords match ✓</p>
                )}
              </div>
              <button type="submit" className="rh-btn-primary" disabled={busy || !token}>
                {busy ? 'Saving…' : 'Set new password'}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Link
                to="/reddit/login"
                style={{ fontSize: 13, color: 'var(--rh-text-muted)', textDecoration: 'none' }}
              >
                ← Back to Sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
